import React, {useEffect, useRef} from 'react';
import {Animated, Easing} from 'react-native';

type Props = {
  /** Only the newest entry animates in; the rest are already there. */
  isNewest: boolean;
  children: React.ReactNode;
};

/**
 * One entry arriving in the coach transcript.
 *
 * The motion exists for one reason: a hint is asked for by pressing a button
 * on the *left*, under the board, and answered on the *right*, in the rail.
 * Without something to follow, a student watching their own pieces misses the
 * answer entirely and presses the button again.
 *
 * Deliberately small — a rise of 8px and a fade over 260ms. Hint can be
 * pressed twice in a row and questions can be asked all afternoon, so this
 * runs on every entry; anything showier would be exhausting by the fourth
 * time. No scale, no bounce, no flashing edges: they draw the eye harder than
 * the content deserves and they read as an alert rather than as an arrival.
 *
 * Opacity and translateY only, so it runs on the native driver and cannot
 * trigger a layout pass on a list that is already scrolling.
 */
export default function CoachTurnEntry({isNewest, children}: Props) {
  // Entries that were already on screen start settled: re-animating the whole
  // transcript every time one is appended would be a wave, not an arrival.
  const progress = useRef(new Animated.Value(isNewest ? 0 : 1)).current;

  useEffect(() => {
    if (!isNewest) return;
    const animation = Animated.timing(progress, {
      toValue: 1,
      duration: 260,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    });
    animation.start();
    return () => animation.stop();
    // Runs once per entry: `isNewest` going false as a later entry arrives
    // must not replay it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Animated.View
      style={{
        gap: 6,
        opacity: progress,
        transform: [
          {
            translateY: progress.interpolate({
              inputRange: [0, 1],
              outputRange: [8, 0],
            }),
          },
        ],
      }}>
      {children}
    </Animated.View>
  );
}
